/* eslint-disable max-len, no-await-in-loop, no-loop-func, no-param-reassign, no-plusplus, padded-blocks */
/* This mixin contains the event handlers for the Project Map object */

import { loadModules } from 'esri-loader';

// https://developers.arcgis.com/javascript/latest/api-reference/esri-views-draw-PolylineDrawAction.html
// https://developers.arcgis.com/javascript/latest/sample-code/draw-line/index.html
// https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#properties-summary
// https://developers.arcgis.com/javascript/latest/sample-code/sketch-geometries/index.html

export default {
  methods: {
    btnManageBoundariesOnClick() {

      this.showEditInstructions = true;

      this.btnDefaultButtonsShow = false;
      this.btnEditCancelSaveShow = true;

      this.sketch = new this.Sketch({
        availableCreateTools: ['polygon'],
        creationMode: 'single',
        defaultCreateOptions: {
          mode: 'click',
        },
        defaultUpdateOptions: {
          enableRotation: false,
          enableScaling: false,
          enableZ: false,
        },
        layer: this.graphicsLayer,
        view: this.view,
      });

      this.sketchCreateHandle = this.sketch.on('create', (event) => {
        if (event.state === 'start') {
          this.selectLayerViewOnClickHandle.remove();
        }
        if (event.state === 'complete') {
          event.graphic.symbol = this.config.projectMap.projectDrawCompleteSymbol;
          this.selectLayerViewOnClickHandle = this.view.on('click', this.selectLayerViewOnClick);
        }
      });

      this.sketchUpdateHandle = this.sketch.on('update', (event) => {
        if (event.state === 'start') {
          this.selectLayerViewOnClickHandle.remove();
        }
        if (event.state === 'complete') {
          this.selectLayerViewOnClickHandle = this.view.on('click', this.selectLayerViewOnClick);
        }
      });

      this.view.ui.add(this.sketch, 'top-left');

      if (this.projectGeometry) {
        const projectGeometries = this.projectGeometry.rings.map((ring) => new this.Graphic({
          geometry: {
            type: 'polygon',
            rings: [ring],
            spatialReference: this.projectGeometry.spatialReference,
          },
          symbol: this.config.projectMap.projectDrawCompleteSymbol,
        }));
        this.graphicsLayer.removeAll();
        this.graphicsLayer.addMany(projectGeometries);
      }

      this.addSelectLayerList();
      this.selectLayerViewOnClickHandle = this.view.on('click', this.selectLayerViewOnClick);
    },
    btnManageSitesOnClick() {

      this.btnDefaultButtonsShow = false;

      this.viewOnClickHandle = this.view.on('click', this.viewOnClick);

      // Begin the edit session with an empty selected sites list
      this.selectedSites = [];
      this.highlightProjectSites();

      this.btnManageSitesButtonsShow = true;
      this.showManageSitesInstructions = true;
    },
    btnManageSitesClearOnClick() {
      this.unhighlightProjectSites();
      this.selectedSites = [];
    },
    btnManageSitesSaveOnClick() {

      this.viewOnClickHandle.remove();

      this.unhighlightProjectSites();

      this.showManageSitesInstructions = false;
      this.btnManageSitesButtonsShow = false;

      this.isSaving = true;

      const projectSiteIdsExpected = this.selectedSites.map((feature) => feature.graphic.attributes.insightlyid).sort();

      this.saveProjectSites().then(async () => {

        let projectSiteIdsActual = this.projectSites.map((projectSite) => projectSite.relatedSiteId).sort();
        let projectSitesDifference = projectSiteIdsExpected.filter(
          (x) => !projectSiteIdsActual.includes(x),
        ).concat(
          projectSiteIdsActual.filter(
            (x) => !projectSiteIdsExpected.includes(x),
          ),
        );

        let retry = 0;

        while ((projectSitesDifference.length !== 0) && (retry < this.config.insightly.retryLimit)) {

          await this.$store.dispatch('Project/getProjectSites', this.project.id);

          projectSiteIdsActual = this.projectSites.map((projectSite) => projectSite.relatedSiteId).sort();
          projectSitesDifference = projectSiteIdsExpected.filter(
            (x) => !projectSiteIdsActual.includes(x),
          ).concat(
            projectSiteIdsActual.filter(
              (x) => !projectSiteIdsExpected.includes(x),
            ),
          );

          retry++;
        }

        this.isSaving = false;

        if (retry >= this.config.insightly.retryLimit) {
          this.showGetProjectSitesWarning();
        }

        if (this.projectSites && this.projectSites.length > 0) {
          this.$refs.projectSitesTable.refresh();
        }

        this.projectSites.forEach((ps) => {
          this.$store.dispatch('Project/getProjectContacts', ps.relatedSiteId);
          this.$store.dispatch('Project/getProjectOrganizations', ps.relatedSiteId);
        });

        this.btnDefaultButtonsShow = true;
      }).catch((exception) => {
        console.error(exception);
      });
    },
    btnManageSitesCancelOnClick() {
      this.viewOnClickHandle.remove();
      this.unhighlightProjectSites();
      this.showManageSitesInstructions = false;
      this.btnManageSitesButtonsShow = false;
      this.btnDefaultButtonsShow = true;
    },
    btnEditClearOnClick() {
      this.graphicsLayer.removeAll();
    },
    btnEditCancelOnClick() {

      this.sketch.layer.removeAll();
      this.sketch.cancel();

      this.view.ui.remove(this.sketch);
      this.sketch.destroy();

      this.selectLayerViewOnClickHandle.remove();

      this.removeSelectLayerListLayer();
      this.removeSelectLayerList();

      this.showEditInstructions = false;
      this.btnEditCancelSaveShow = false;

      if (this.projectGeometry == null) {
        this.setExtentByDefault();
      } else {
        this.loadProjectGraphic(this.projectGeometry);
        this.setExtentByProjectGeometry(this.projectGeometry);
      }

      this.btnDefaultButtonsShow = true;

    },
    btnEditSaveOnClick() {
      if (this.graphicsLayer.graphics.items.length === 0) {
        this.showEmptyProjectSitesWarning = true;
      } else {
        this.view.ui.remove(this.sketch);
        this.sketch.destroy();
        this.selectLayerViewOnClickHandle.remove();
        this.removeSelectLayerListLayer();
        this.removeSelectLayerList();
        this.showEditInstructions = false;
        this.btnEditCancelSaveShow = false;
        this.saveProjectGeometry();
      }
    },
    btnEditSelectBoundaryOnClick() {
      this.selectLayerHits.map((item) => item.hit.graphic).forEach((graphic) => {
        graphic.symbol = this.config.projectMap.projectDrawCompleteSymbol;
        this.graphicsLayer.add(graphic);
      });
      this.removeSelectLayerHitHighlights();
    },
    onProjectGeometrySet() {
      if (this.projectGeometry != null) {
        this.loadProjectGraphic(this.projectGeometry);
        this.setExtentByProjectGeometry(this.projectGeometry);
      } else if (
        this.project.categoryId === this.config.projectMap.consEdCategoryId
        && (this.project.consEdStreet || this.project.consEdCity || this.project.consEdZip)
      ) {
        this.setExtentByProjectAddress(this.project);
      } else {
        this.setExtentByDefault();
        if (this.project.categoryId === this.config.projectMap.consEdCategoryId) {
          this.showGeocodeWarning();
        }
      }
    },
    radioButtonOnClick(event) {
      this.view.map = this.maps[event.target.value];
      this.layers.forEach((layer) => this.view.map.add(layer));
      this.webmapListExpand.toggle();
    },
    selectLayerItemRadioButtonOnClick(event) {
      this.removeSelectLayerListLayer();
      this.removeSelectLayerHitHighlights();
      if (event.target.value >= 0) {
        loadModules(['esri/layers/FeatureLayer']).then(([FeatureLayer]) => {
          this.selectLayer = new FeatureLayer(
            this.config.projectMap.selectableLayers[event.target.value],
          );
          this.view.map.layers.add(this.selectLayer);
        });
      }
    },
    selectLayerViewOnClick(event) {
      if (this.selectLayer) {

        const selectLayerHitObjectIds = this.selectLayerHits.map(
          (item) => item.hit.graphic.attributes.objectid,
        );

        this.removeSelectLayerHitHighlights();

        this.view.hitTest(event).then((response) => {
          this.view.whenLayerView(this.selectLayer).then((layerView) => {

            let hits = response.results.filter(
              (feature) => feature.graphic.layer.id === this.selectLayerId,
            );

            hits = hits.filter((hit) => {
              const value = !(selectLayerHitObjectIds.includes(hit.graphic.attributes.objectid));
              return value;
            });

            hits.forEach((hit) => {
              this.selectLayerHits.push({
                hit,
                highlight: layerView.highlight(hit.graphic),
              });
            });
          });
        });
      }
    },
    viewOnClick(event) {
      this.view.hitTest(event).then((response) => {
        const layer = this.siteLayer;

        const hits = response.results.filter(
          (feature) => feature.graphic.layer.id === this.siteLayer.id,
        );

        this.view.whenLayerView(layer).then((layerView) => {
          hits.forEach((hit) => {
            const filteredSites = this.selectedSites.filter(
              (site) => site.graphic.attributes.objectid === hit.graphic.attributes.objectid,
            );
            if (filteredSites.length > 0) {
              filteredSites.forEach((filteredSite) => {
                filteredSite.highlight.remove();
              });
              this.selectedSites = this.selectedSites.filter(
                (site) => site.graphic.attributes.objectid !== hit.graphic.attributes.objectid,
              );
            } else {
              const query = layer.createQuery();
              query.objectIds = [hit.graphic.attributes.objectid];
              layer.queryFeatures(query).then((results) => {
                this.selectedSites.push({
                  graphic: results.features[0],
                  highlight: layerView.highlight(hit.graphic),
                });
              });
            }
          });
        });
      });
    },
  },
};
